import { type ExpressionBuilder, type Transaction } from "@rocicorp/zero";
import { defineQuery } from "../../zero/transformer";
import { zql, type Schema } from "../../zero/schema";
import { AccessGrant } from "@corpuscomplete/core/auth/grant";
import { z } from "zod/v4";
import { withZeroTransaction } from "../../zero/utils";
import { type UserActor } from "@corpuscomplete/core/actor";
import { accessDenied } from "@corpuscomplete/core/utils";
import { foodTypes, type FoodType } from "../food/constants";
import { Temporal } from "temporal-polyfill";
import { foodCollectionTypes } from "./constants";
import { ScopePolicy } from "../../auth/scope/policy";

/**
 * Food-collection namespace boundary for visibility, grants, and move policies.
 *
 * Invariants:
 * - Public collections are viewable; private collections require matching grants.
 * - Recipe/curated variation moves preserve collection consistency constraints.
 *
 * @see `packages/core/docs/namespace.md`
 */
export namespace FoodCollection {
  const MAX_NAME_LENGTH = 100;

  export type Type = (typeof foodCollectionTypes)[number];

  type MoveTarget = {
    id: string;
    version: number;
  };

  type MovePolicyInput = {
    tx: Transaction;
    food: {
      id: string;
      version: number;
      type: FoodType;
      collectionId: string;
    };
  };

  const movePolicyByFoodType: Record<FoodType, (input: MovePolicyInput) => Promise<MoveTarget[]>> =
    {
      generic: async ({ food }) => [{ id: food.id, version: food.version }],
      branded: async ({ food }) => [{ id: food.id, version: food.version }],
      recipe: async ({ tx, food }) => {
        const curatedVariations = await tx.run(
          zql.recipeVariation
            .where("recipeId", "=", food.id)
            .where("recipeVersion", "=", food.version)
            .where("kind", "=", "curated"),
        );

        const targets: MoveTarget[] = [{ id: food.id, version: food.version }];
        for (const variant of curatedVariations) {
          const currentVariant = await tx.run(
            zql.foodMeta
              .where("id", "=", variant.id)
              .where("timeArchived", "IS", null)
              .related("current")
              .one(),
          );

          if (!currentVariant?.current) {
            continue;
          }

          if (currentVariant.collectionId !== food.collectionId) {
            throw new Error("Referenced recipe variant must be in the same collection as recipe");
          }

          targets.push({ id: currentVariant.id, version: currentVariant.current.version });
        }

        return targets;
      },
      recipe_variation: async ({ tx, food }) => {
        const variant = await tx.run(
          zql.recipeVariation.where("id", "=", food.id).where("version", "=", food.version).one(),
        );
        if (!variant) {
          accessDenied("Recipe variant not found");
        }
        if (variant.kind !== "custom") {
          throw new Error("Curated recipe variants must be moved with their base recipe");
        }

        return [{ id: food.id, version: food.version }];
      },
    };

  export function allowsFoodType(
    collection: { allowedFoodTypes: FoodType[] | null | undefined },
    foodType: FoodType,
  ) {
    return collection.allowedFoodTypes === null || collection.allowedFoodTypes === undefined
      ? true
      : collection.allowedFoodTypes.includes(foodType);
  }

  export const forContents = defineQuery(({ ctx }) => {
    return zql.foodCollection.where((e) => viewableContents(e, ctx.actor));
  });

  function viewableContents(eb: ExpressionBuilder<"foodCollection", Schema>, actor: UserActor) {
    return eb.or(
      eb.cmp("visibility", "=", "public"),
      eb.exists("grants", (r) =>
        AccessGrant.matching(
          r,
          actor,
          AccessGrant.criteria("food_collection", "contents", ["editor", "viewer"]),
        ),
      ),
    );
  }

  export const Permissions = AccessGrant.definePermissions({
    canRename: AccessGrant.criteria("food_collection", "root", ["owner", "editor"]),
    canDelete: AccessGrant.criteria("food_collection", "root", ["owner"]),
    canAddContent: AccessGrant.criteria("food_collection", "contents", ["editor"]),
    canMoveContent: AccessGrant.criteria("food_collection", "contents", ["editor"]),
  });

  export const forContentManagement = defineQuery(({ ctx }) => {
    return zql.foodCollection.where((e) => hasContentsEditorGrant(e, ctx.actor));
  });

  function hasContentsEditorGrant(
    eb: ExpressionBuilder<"foodCollection", Schema>,
    actor: UserActor,
  ) {
    return eb.exists("grants", (r) =>
      AccessGrant.matching(
        r,
        actor,
        AccessGrant.criteria("food_collection", "contents", ["editor"]),
      ),
    );
  }

  export const forRootManagement = defineQuery(({ ctx }) => {
    return zql.foodCollection
      .where((e) => viewableRoot(e, ctx.actor))
      .related("grants", (q) =>
        AccessGrant.matching(
          q,
          ctx.actor,
          AccessGrant.criteria("food_collection", "root", ["owner", "editor", "viewer"]),
        ),
      )
      .related("foodMetas", (q) => q.where("timeArchived", "IS", null).related("current"));
  });

  function viewableRoot({ exists }: ExpressionBuilder<"foodCollection", Schema>, actor: UserActor) {
    return exists("grants", (r) =>
      AccessGrant.matching(
        r,
        actor,
        AccessGrant.criteria("food_collection", "root", ["owner", "editor", "viewer"]),
      ),
    );
  }

  export function defaultForUser(userId: string) {
    return zql.foodCollection.where("defaultForUserId", "=", userId).one();
  }

  export type CreateMyDefault = z.infer<typeof createMyDefault.schema>;
  export const createMyDefault = withZeroTransaction(
    z.object({ id: z.uuid() }),
    async ({ tx, ctx, args: { id } }) => {
      const existing = await tx.run(FoodCollection.defaultForUser(ctx.actor.userId));
      if (existing) {
        throw new Error("Default collection already exists for user");
      }
      await tx.mutate.foodCollection.insert({
        id,
        scopeId: ctx.actor.userId,
        name: "My Foods",
        visibility: "private",
        type: "user",
        allowedFoodTypes: [...foodTypes],
        defaultForUserId: ctx.actor.userId,
      });
      await Promise.all([
        // TODO: Consider not giving the owner role to the user here?
        // Would imply that the user can't change roles, which might already be a control I add to default for user collections
        AccessGrant.add(tx, AccessGrant.assignment(id, "food_collection", "root", "owner"), {
          type: "user",
          id: ctx.actor.userId,
        }),
        AccessGrant.add(tx, AccessGrant.assignment(id, "food_collection", "contents", "editor"), {
          type: "user",
          id: ctx.actor.userId,
        }),
      ]);
    },
  );

  export type Create = z.infer<typeof create.schema>;
  export const create = withZeroTransaction(
    z.object({
      id: z.uuid(),
      scopeId: z.uuid(),
      name: z.string().min(1).max(MAX_NAME_LENGTH),
    }),
    async ({ tx, ctx, args: { id, scopeId, name } }) => {
      await ScopePolicy.requireWritable(tx, ctx.actor, scopeId, ["user", "space"]);
      await tx.mutate.foodCollection.insert({
        id,
        scopeId,
        name: name.trim(),
        visibility: "private",
        type: "user",
        allowedFoodTypes: null,
        defaultForUserId: null,
      });
      await Promise.all([
        AccessGrant.add(tx, AccessGrant.assignment(id, "food_collection", "root", "owner"), {
          type: "user",
          id: ctx.actor.userId,
        }),
        AccessGrant.add(tx, AccessGrant.assignment(id, "food_collection", "contents", "editor"), {
          type: "user",
          id: ctx.actor.userId,
        }),
      ]);
    },
  );

  export async function createSessionWorkingCollection(
    tx: Transaction,
    args: { id: string; scopeId: string },
  ): Promise<void> {
    await tx.mutate.foodCollection.insert({
      id: args.id,
      scopeId: args.scopeId,
      name: "Session foods",
      visibility: "private",
      type: "user",
      allowedFoodTypes: [...foodTypes],
      defaultForUserId: null,
    });
  }

  async function hasRootGrant(
    tx: Transaction,
    actor: UserActor,
    collectionId: string,
    roles: ("owner" | "editor" | "viewer")[],
  ): Promise<boolean> {
    const result = await tx.run(
      AccessGrant.matching(
        zql.accessGrant.where("resourceId", "=", collectionId),
        actor,
        AccessGrant.criteria("food_collection", "root", roles),
      ).one(),
    );
    return !!result;
  }

  export type Rename = z.infer<typeof rename.schema>;
  export const rename = withZeroTransaction(
    z.object({
      id: z.uuid(),
      name: z.string().min(1).max(MAX_NAME_LENGTH),
    }),
    async ({ tx, ctx, args: { id, name } }) => {
      const collection = await tx.run(zql.foodCollection.where("id", "=", id).one());
      if (!collection) {
        accessDenied("Collection not found");
      }

      if (collection.defaultForUserId !== null) {
        throw new Error("Cannot rename default collection");
      }

      const hasPermission = await hasRootGrant(tx, ctx.actor, id, ["owner", "editor"]);
      if (!hasPermission) {
        accessDenied("Cannot rename collection");
      }

      await tx.mutate.foodCollection.update({
        id,
        name: name.trim(),
      });
    },
  );

  export type Delete = z.infer<typeof del.schema>;
  export const del = withZeroTransaction(
    z.object({ id: z.uuid() }),
    async ({ tx, ctx, args: { id } }) => {
      const collection = await tx.run(
        zql.foodCollection.where("id", "=", id).related("grants").one(),
      );
      if (!collection) {
        accessDenied("Collection not found");
      }

      if (collection.defaultForUserId !== null) {
        throw new Error("Cannot delete default collection");
      }

      const hasPermission = await hasRootGrant(tx, ctx.actor, id, ["owner"]);
      if (!hasPermission) {
        accessDenied("Cannot delete collection");
      }

      const foodCount = await tx.run(
        zql.foodMeta.where("collectionId", "=", id).where("timeArchived", "IS", null),
      );
      if (foodCount.length > 0) {
        throw new Error("Cannot delete collection. Remove all items first.");
      }

      await Promise.all(
        collection.grants.map((grant) => tx.mutate.accessGrant.delete({ id: grant.id })),
      );
      await tx.mutate.foodCollection.delete({ id });
    },
  );

  export type MoveFood = z.infer<typeof moveFood.schema>;
  export const moveFood = withZeroTransaction(
    z.object({
      foodId: z.uuid(),
      targetCollectionId: z.uuid(),
    }),
    async ({ tx, ctx, args: { foodId, targetCollectionId } }) => {
      const foodMeta = await tx.run(zql.foodMeta.where("id", "=", foodId).related("current").one());
      if (!foodMeta?.current || foodMeta.timeArchived) {
        accessDenied("Food not found");
      }

      const hasSourceContentsEditor = await tx.run(
        AccessGrant.matching(
          zql.accessGrant.where("resourceId", "=", foodMeta.collectionId),
          ctx.actor,
          AccessGrant.criteria("food_collection", "contents", ["editor"]),
        ).one(),
      );
      if (!hasSourceContentsEditor) {
        accessDenied("Cannot move food from collection");
      }

      const targetCollection = await tx.run(
        zql.foodCollection.where("id", "=", targetCollectionId).one(),
      );
      if (!targetCollection) {
        accessDenied("Target collection not found");
      }

      const hasContentsEditor = await tx.run(
        AccessGrant.matching(
          zql.accessGrant.where("resourceId", "=", targetCollectionId),
          ctx.actor,
          AccessGrant.criteria("food_collection", "contents", ["editor"]),
        ).one(),
      );
      if (!hasContentsEditor) {
        accessDenied("Cannot move food to collection");
      }

      const externalRef = await tx.run(
        zql.externalFoodIdentity.where("foodId", "=", foodMeta.id).one(),
      );
      if (externalRef) {
        throw new Error("Externally linked foods cannot be moved between collections");
      }

      const moveTargets = await movePolicyByFoodType[foodMeta.type]({
        tx,
        food: {
          id: foodMeta.id,
          version: foodMeta.current.version,
          type: foodMeta.type,
          collectionId: foodMeta.collectionId,
        },
      });
      const timeNow = foodMeta.timeUpdated.add({
        milliseconds:
          Temporal.Now.instant().epochMilliseconds - foodMeta.timeUpdated.epochMilliseconds,
      });

      // Simplified policy: any cascaded move targets must currently live in the same collection
      // as the primary food being moved.
      const targetFoods = await Promise.all(
        moveTargets.map((target) =>
          tx.run(
            zql.food
              .where("id", "=", target.id)
              .where("version", "=", target.version)
              .related("meta")
              .one(),
          ),
        ),
      );
      for (const targetFood of targetFoods) {
        if (!targetFood) {
          continue;
        }
        if (targetFood.meta?.collectionId !== foodMeta.collectionId) {
          throw new Error(
            "Referenced foods must be in the same collection as the food being moved",
          );
        }
      }

      await Promise.all(
        moveTargets.map((target) =>
          tx.mutate.foodMeta.update({
            id: target.id,
            collectionId: targetCollectionId,
            timeUpdated: timeNow,
          }),
        ),
      );
    },
  );
}
